// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

// doc-exempt: a copy of AYNI 3D's src/shared/wayra-game/sky.ts; never edit it here (scripts/sync-ayni3d-copy.ts)
/**
 * Sky and map arithmetic for "Wayra's Adventures" clues
 * (docs/KIOSK_GAMES_DESIGN.md §5.4, "Sky and map" facts): where the sun is
 * at a place right now, and how far and which way Wayra ran. Everything is
 * computed from latitude, longitude and the time, so these facts carry no
 * data risk. Accuracy is about half a degree of sun elevation (NOAA's
 * approximations), far inside the facts' margins.
 */

const DEG = Math.PI / 180;
const EARTH_RADIUS_KM = 6371;

function dayOfYear(date: Date): number {
  const start = Date.UTC(date.getUTCFullYear(), 0, 1);
  return Math.floor((date.getTime() - start) / 86_400_000) + 1;
}

/** The sun's elevation above the horizon in degrees (negative below it). */
export function solarElevationDeg(lat: number, lon: number, epochMs: number): number {
  const date = new Date(epochMs);
  const hours = date.getUTCHours() + date.getUTCMinutes() / 60 + date.getUTCSeconds() / 3600;
  const gamma = (2 * Math.PI / 365) * (dayOfYear(date) - 1 + (hours - 12) / 24);
  const eqTimeMin = 229.18 * (0.000075 + 0.001868 * Math.cos(gamma) - 0.032077 * Math.sin(gamma)
    - 0.014615 * Math.cos(2 * gamma) - 0.040849 * Math.sin(2 * gamma));
  const decl = 0.006918 - 0.399912 * Math.cos(gamma) + 0.070257 * Math.sin(gamma)
    - 0.006758 * Math.cos(2 * gamma) + 0.000907 * Math.sin(2 * gamma)
    - 0.002697 * Math.cos(3 * gamma) + 0.00148 * Math.sin(3 * gamma);
  const trueSolarMin = hours * 60 + eqTimeMin + 4 * lon;
  const hourAngle = (trueSolarMin / 4 - 180) * DEG;
  const latR = lat * DEG;
  const cosZenith = Math.sin(latR) * Math.sin(decl) + Math.cos(latR) * Math.cos(decl) * Math.cos(hourAngle);
  return 90 - Math.acos(Math.max(-1, Math.min(1, cosZenith))) / DEG;
}

/** Great-circle distance in km. */
export function distanceKm(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const dLat = (b.lat - a.lat) * DEG;
  const dLon = (b.lon - a.lon) * DEG;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * DEG) * Math.cos(b.lat * DEG) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Initial bearing from a to b in degrees clockwise from north (0..360). */
export function bearingDeg(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const la = a.lat * DEG;
  const lb = b.lat * DEG;
  const dLon = (b.lon - a.lon) * DEG;
  const y = Math.sin(dLon) * Math.cos(lb);
  const x = Math.cos(la) * Math.sin(lb) - Math.sin(la) * Math.cos(lb) * Math.cos(dLon);
  return ((Math.atan2(y, x) / DEG) + 360) % 360;
}

export const COMPASS_POINTS = ['north', 'north-east', 'east', 'south-east', 'south', 'south-west', 'west', 'north-west'] as const;
export type CompassPoint = typeof COMPASS_POINTS[number];

/**
 * The 8-point compass direction of a bearing, or null inside the 10° dead
 * zone at each sector edge (so "towards the east" is never a coin toss).
 */
export function compassPoint(bearing: number): CompassPoint | null {
  const sector = ((bearing % 360) + 360 + 22.5) % 360;
  const within = sector % 45;
  if (within < 5 || within > 40) return null;
  return COMPASS_POINTS[Math.floor(sector / 45)] ?? null;
}

/** Month (1-12) in UTC. */
export function monthOf(epochMs: number): number {
  return new Date(epochMs).getUTCMonth() + 1;
}

/**
 * The season at a place, only outside the tropics (beyond 28°) and only in
 * the clear months: June-August and December-February. Elsewhere null.
 */
export function clearSeason(lat: number, epochMs: number): 'summer' | 'winter' | null {
  if (Math.abs(lat) < 28) return null;
  const month = monthOf(epochMs);
  const northSummer = month >= 6 && month <= 8;
  const northWinter = month === 12 || month <= 2;
  if (!northSummer && !northWinter) return null;
  const north = lat > 0;
  return northSummer === north ? 'summer' : 'winter';
}
